import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { memo, useEffect, useMemo, useState } from "react";
import { Text, Group, TextInput, CloseButton, Grid, Box, Button, } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import Fuse from "fuse.js";
import { isEmpty } from "lodash";
import { SearchIcon as SearchIconSvg, RoundPlusBlackX as CloseIcon, } from "../assets/icons.generated";
import { SECONDARY_COLOR } from "../theme/button";
import { TEXT_COLOR } from "../theme/colors";
import { useFuzzySearchTerms } from "./context";
const SearchTermTag = ({ tableName, searchTerm, matchCount, }) => {
    const { dispatch } = useFuzzySearchTerms();
    return (_jsx(Box, { sx: (theme) => ({
            backgroundColor: theme.colors.gray[0],
            borderRadius: "6px",
        }), children: _jsxs(Group, { spacing: "xs", px: "xs", children: [_jsx(Text, { size: "xs", weight: 500, pl: 4, children: searchTerm }), _jsx(Text, { size: "xs", color: TEXT_COLOR.SECONDARY, children: matchCount ? matchCount : "0" }), _jsx(CloseButton, { size: "sm", onClick: () => dispatch({
                        type: "REMOVE_TERM",
                        payload: { tableName, searchTerm },
                    }), "aria-label": "Remove search-tag" })] }) }));
};
const FUSE_FUZZY_MATCH_DISTANCE_AUDIENCE = 0.33;
const DELAY_FOR_USER_TO_FINISH_TYPING = 350; // milliseconds
const FUSE_MATCH_KEY = "fuse_match_entity_id";
export const FuzzyFilterSearchBar = ({ tableName, data, prompt, fuzzyKeys, sortButton, onSearchTermsChange, }) => {
    var _a;
    const { state, dispatch } = useFuzzySearchTerms();
    const [filterString, setFilterString] = useState("");
    const [prevFilterString, setPrevFilterString] = useState("");
    const [debouncedFilterString] = useDebouncedValue(filterString, DELAY_FOR_USER_TO_FINISH_TYPING);
    const { tableSearchTerms } = state;
    const searchTerms = (_a = tableSearchTerms[tableName]) !== null && _a !== void 0 ? _a : {};
    useEffect(() => {
        onSearchTermsChange === null || onSearchTermsChange === void 0 ? void 0 : onSearchTermsChange(searchTerms);
    }, [state]);
    useEffect(() => {
        if (debouncedFilterString) {
            const matches = runFuzzySearch(debouncedFilterString);
            // dynamically show match-count while user keeps typing
            if (prevFilterString) {
                dispatch({
                    type: "SWAP_TERM",
                    payload: {
                        tableName,
                        searchTerm: debouncedFilterString,
                        matchedEntityIds: matches,
                        remove: prevFilterString,
                    },
                });
            }
            else {
                dispatch({
                    type: "ADD_TERM",
                    payload: {
                        tableName,
                        searchTerm: debouncedFilterString,
                        matchedEntityIds: matches,
                    },
                });
            }
            setPrevFilterString(debouncedFilterString);
            // save count
        }
    }, [debouncedFilterString]);
    const fuzzyIndex = useMemo(() => {
        const flattenData = [];
        for (const key of Object.keys(data)) {
            const entityData = { ...data[key] };
            // do it this way so key is actual string "fuse_match_entity_id" vs const-var-name "FUSE_MATCH_KEY"
            entityData[FUSE_MATCH_KEY] = key;
            flattenData.push(entityData);
        }
        const options = {
            // isCaseSensitive: false,
            includeScore: true,
            // shouldSort: true,
            includeMatches: true,
            // findAllMatches: false,
            minMatchCharLength: 2,
            // location: 0,
            /**
             * Threshold of 0.0 => perfect exact match
             * Threshold of 1.0 => any string
             */
            threshold: FUSE_FUZZY_MATCH_DISTANCE_AUDIENCE,
            // distance: 100,
            // useExtendedSearch: false,
            ignoreLocation: true,
            // ignoreFieldNorm: false,
            keys: fuzzyKeys,
        };
        const myIndex = Fuse.createIndex(options.keys, flattenData);
        return new Fuse(flattenData, options, myIndex);
    }, [data]);
    const runFuzzySearch = (filterString) => {
        // bail early
        if (!filterString) {
            return [];
        }
        /* eslint-disable  @typescript-eslint/no-explicit-any */
        const searchResults = fuzzyIndex.search(filterString);
        // return the unique keys for each table-data-row's entity
        return searchResults.map((match) => {
            return match.item[FUSE_MATCH_KEY];
        });
    };
    return (_jsxs(_Fragment, { children: [_jsxs(Grid, { columns: 48, align: "stretch", children: [_jsx(Grid.Col, { span: !sortButton ? 48 : 37, children: _jsx(TextInput, { placeholder: prompt, onChange: (e) => setFilterString(e.currentTarget.value), onKeyPress: (e) => {
                                // save search term, clear search bar to add more
                                if (e.key === "Enter") {
                                    e.currentTarget.value = "";
                                    setPrevFilterString("");
                                    setFilterString("");
                                }
                            }, onBlur: (e) => {
                                e.currentTarget.value = "";
                                setPrevFilterString("");
                                setFilterString("");
                            }, icon: _jsx(SearchIconSvg, {}) }) }), sortButton && _jsx(Grid.Col, { span: 9, children: sortButton })] }), !isEmpty(searchTerms) && (_jsxs(Grid, { justify: "flex-end", grow: true, children: [_jsx(Grid.Col, { span: 9, children: _jsx(Group, { position: "left", children: Object.keys(searchTerms).map((st) => {
                                var _a, _b;
                                return (_jsx(SearchTermTag, { tableName: tableName, searchTerm: st, matchCount: (_b = (_a = searchTerms[st]) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0 }, st));
                            }) }) }), _jsx(Grid.Col, { span: "content", children: _jsx(Group, { position: "left", children: _jsx(Button, { color: SECONDARY_COLOR, size: "xs", onClick: () => {
                                    setPrevFilterString("");
                                    dispatch({ type: "REMOVE_ALL", payload: { tableName } });
                                }, leftIcon: _jsx(CloseIcon, {}), children: "Clear all" }) }) })] }))] }));
};
export default memo(FuzzyFilterSearchBar);
